import { Fragment, MouseEvent, ReactNode, useState } from 'react';
import {
  alpha,
  Box,
  Checkbox,
  Stack,
  styled,
  TableRow as MuiTableRow,
  tableRowClasses,
  TableRowProps as MuiTableRowProps,
} from '@mui/material';
import classNames from 'classnames';

import { Icon, Menu, MenuItemProps } from '@components/base';
import useHighlightComponent from '@hooks/useHighlightComponent';

import { TableCell } from './TableCell';
import { IdKey, SelectableProps, TableColumn } from './types';

const highlightTransition = 500;

const StyledTableRow = styled(MuiTableRow)(({ theme }) => ({
  backgroundColor: 'transparent',
  '&.fade': { transition: `background-color ${highlightTransition}ms` },
  '&.clickable': { cursor: 'pointer' },
  '&.marked': { background: alpha(theme.palette.info[theme.palette.mode], 0.5) },
  '&.highlighted': {
    background: alpha(
      theme.palette.mode === 'dark' ? theme.palette.success.dark : theme.palette.warning.light,
      0.5
    ),
    [`&.${tableRowClasses.hover}:hover`]: {
      background: alpha(
        theme.palette.mode === 'dark' ? theme.palette.success.dark : theme.palette.warning.light,
        0.7
      ),
    },
  },

  [`&.${tableRowClasses.hover}:hover, &.shadow`]: {
    boxShadow:
      theme.palette.mode === 'dark'
        ? 'inset 1px 0 0 rgba(255, 255, 255, 0.2), inset -1px 0 0 rgba(255, 255, 255, 0.2), 0 0 4px 0 rgba(95, 99, 104, 0.6), 0 0 6px 2px rgba(95, 99, 104, 0.6)'
        : 'inset 1px 0 0 #dadce0, inset -1px 0 0 #dadce0, 0 1px 2px 0 rgba(60,64,67,.3), 0 1px 3px 1px rgba(60,64,67,.15)',
  },
}));

export type TableRowProps<T> = IdKey<T> &
  MuiTableRowProps & {
    columns?: TableColumn<T>[];
    getActions?: (element: T) => ReactNode;
    hasActions?: boolean;
    hasOptionsButton?: boolean;
    highlighted: boolean;
    item: T;
    marked?: boolean;
    options?: MenuItemProps[];
  };

export function TableRow<T>({
  className,
  columns,
  getActions,
  hasActions,
  hasOptionsButton,
  highlighted: initHighlighted,
  idKey,
  item,
  marked,
  options,
  selectable,
  ...rest
}: TableRowProps<T> & SelectableProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);

  const handleContextMenu = (event: MouseEvent) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null
    );
  };

  const handleClose = () => setContextMenu(null);

  const itemId = item[idKey] as string;

  const isSelected = selectable?.selectedIds.includes(itemId);
  const rowTitleId = `row-${itemId}-title`;
  const hasOptions = options && options.length > 0;
  const actions = getActions?.(item);
  const hasActionColumn = hasOptionsButton || hasActions || actions;

  const { isFading, isHighlighted } = useHighlightComponent(initHighlighted);

  return (
    <Fragment>
      <StyledTableRow
        className={classNames(className, {
          clickable: rest.onClick || selectable,
          fade: isFading,
          marked: marked || menuOpen || contextMenu,
          shadow: menuOpen || contextMenu,
          highlighted: isHighlighted,
        })}
        hover
        id={itemId}
        onContextMenu={hasOptions ? handleContextMenu : undefined}
        {...(selectable
          ? {
              onClick: () => selectable.handleSelect(itemId),
              role: 'checkbox',
              'aria-checked': isSelected,
              selected: isSelected,
            }
          : {})}
        {...rest}
      >
        {selectable && (
          <TableCell padding='checkbox'>
            <Checkbox
              checked={isSelected}
              color='info'
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                selectable.handleSelect(itemId);
              }}
              slotProps={{ input: { 'aria-labelledby': rowTitleId } }}
            />
          </TableCell>
        )}

        {columns?.map(column => {
          const {
            TableCellProps: tableCellProps = { sx: { maxWidth: { xs: 150, md: 200, lg: 'none' } } },
            value,
            isTypeColumn,
          } = column;

          const displayValue =
            typeof value === 'string'
              ? (item[value] as string)?.toString()
              : typeof value === 'function'
                ? value(item)
                : '';

          return (
            <TableCell
              key={column.key}
              {...{
                ...tableCellProps,
                ...(isTypeColumn ? { minimalWidth: true } : {}),
              }}
            >
              {isTypeColumn ? (
                <Box alignItems='center' display='flex' justifyContent='center' pr={2}>
                  {displayValue}
                </Box>
              ) : (
                displayValue
              )}
            </TableCell>
          );
        })}

        {hasActionColumn && (
          <TableCell className='table__actions' minimalWidth>
            <Stack direction='row'>
              {actions}

              {hasOptions && (
                <Menu
                  {...(contextMenu
                    ? { open: true, onClose: handleClose }
                    : { onClose: () => setMenuOpen(false) })}
                >
                  {hasOptionsButton && (
                    <Menu.IconButton
                      onClick={e => {
                        e.stopPropagation();
                        setMenuOpen(true);
                      }}
                      size='small'
                    >
                      <Icon fontSize='small' name='moreHoriz' />
                    </Menu.IconButton>
                  )}

                  <Menu.Content
                    {...(contextMenu
                      ? {
                          anchorReference: 'anchorPosition',
                          anchorPosition: { top: contextMenu.mouseY, left: contextMenu.mouseX },
                        }
                      : {})}
                  >
                    {options?.map(({ key, ...rest }) => (
                      <Menu.Item key={key || JSON.stringify(rest)} {...rest} />
                    ))}
                  </Menu.Content>
                </Menu>
              )}
            </Stack>
          </TableCell>
        )}
      </StyledTableRow>
    </Fragment>
  );
}
